import React, { useEffect, useState, ReactNode } from 'react';
import LoadingScreen from '../common/LoadingScreen';
import ErrorScreen from '../common/ErrorScreen';

interface DomainResolverProps {
  domain?: string;
  subdomain?: string;
  children: ReactNode;
}

interface DomainInfo {
  photographerId: string;
  domain: string;
  subdomain?: string;
  customDomain?: string;
  isValid: boolean;
}

const DomainResolver: React.FC<DomainResolverProps> = ({ domain, subdomain, children }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [domainInfo, setDomainInfo] = useState<DomainInfo | null>(null);

  useEffect(() => {
    const resolveDomain = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get domain information from current URL
        const hostname = window.location.hostname;

        let resolvedDomain: string;
        let resolvedSubdomain: string | undefined;

        // Determine if this is a custom domain or subdomain
        if (domain && subdomain) {
          // Explicitly provided
          resolvedDomain = domain;
          resolvedSubdomain = subdomain;
        } else if (subdomain) {
          resolvedSubdomain = subdomain;
          resolvedDomain = `${subdomain}.pixel-share.com`;
        } else if (hostname.includes('.pixel-share.com')) {
          // Subdomain pattern: {photographer}.pixel-share.com
          const parts = hostname.split('.');
          if (parts.length === 3 && parts[1] === 'pixel-share' && parts[2] === 'com') {
            resolvedSubdomain = parts[0];
            resolvedDomain = hostname;
          } else {
            throw new Error('Invalid subdomain format');
          }
        } else {
          // Custom domain
          resolvedDomain = hostname;
        }

        // Validate domain and get photographer information
        // This would typically make an API call to resolve the domain
        const domainData: DomainInfo = {
          photographerId: resolvedSubdomain || resolvedDomain.replace(/\./g, '_'),
          domain: resolvedDomain,
          subdomain: resolvedSubdomain,
          customDomain: resolvedSubdomain ? undefined : resolvedDomain,
          isValid: true,
        };

        // Store domain info in sessionStorage for other components
        sessionStorage.setItem('gallery_domain_info', JSON.stringify(domainData));

        setDomainInfo(domainData);
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to resolve domain');
        setLoading(false);
      }
    };

    resolveDomain();
  }, [domain, subdomain]);

  if (loading) {
    return <LoadingScreen message="Loading gallery..." />;
  }

  if (error || !domainInfo?.isValid) {
    return (
      <ErrorScreen
        title="Gallery Not Found"
        message={error || "This gallery doesn't exist or is not available."}
        showRetry
        onRetry={() => window.location.reload()}
      />
    );
  }

  return <>{children}</>;
};

export default DomainResolver;
